import { useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useSessionActions, useCurrentSession } from '@/stores/gameSessionStore';
import { useStudentAutoNavigation } from '@/hooks/useStudentAutoNavigation';
import { StudentDebugPanel } from './StudentDebugPanel';
import { getStudentUrlParams, validateStudentUrlParams, isStudentRoute, debugUrlState } from '@/utils/studentUrlHelpers';
import { useRobustUrlParams } from '@/hooks/useRobustUrlParams';
import { useSessionRecovery, updateSessionActivity, isExitInProgress } from '@/utils/sessionRecovery';
import { dbService } from '@/services/database';
import { arenaRealtimeService } from '@/services/realtime';
import logger from '@/utils/logger';

interface StudentGameSessionMonitorProps {
  children: ReactNode;
}

export const StudentGameSessionMonitor: FC<StudentGameSessionMonitorProps> = ({ children }) => {
  const location = useLocation();
  const { params: robustParams, diagnostics: urlDiagnostics } = useRobustUrlParams();
  const sessionId = robustParams.sessionId;
  const playerId = robustParams.playerId;
  const { selectSession, refreshSession, setCurrentSessionDirect } = useSessionActions();
  const currentSession = useCurrentSession();
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const latestSessionStateRef = useRef<string | null>(null);
  const rtdbActiveRef = useRef(false);
  const prevGameStateRef = useRef<string | null>(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const { validateSessionConsistency } = useSessionRecovery();

  // Enhanced URL parameter handling
  const urlParams = getStudentUrlParams();
  const urlValidation = validateStudentUrlParams(urlParams);
  const isStudentRoutePath = isStudentRoute(location.pathname);

  // Check if user is on routes where they explicitly chose to exit
  const isOnTeacherRoute = location.pathname.startsWith('/teacher');
  const isOnMainLobby = location.pathname === '/' && !window.location.search;
  const exitInProgress = isExitInProgress();
  const userHasExplicitlyExited = isOnTeacherRoute || isOnMainLobby || exitInProgress;

  // /join-game is a valid place for students while gameState is 'lobby'.
  // Auto-navigation would see a route mismatch (/join-game vs /lobby) and redirect,
  // which breaks the join flow. So we suppress auto-nav on /join-game ONLY while
  // the game is still in lobby state. Once the teacher starts the game (gameState
  // changes to 'countdown' or anything else), auto-nav kicks in and moves students.
  const isOnJoinGameInLobby = location.pathname.startsWith('/join-game') &&
                               (!currentSession || currentSession.gameState === 'lobby');

  // IMPORTANT: Separate two concerns:
  // 1. shouldMonitorSession - Controls real-time Firebase listener (always on when student has sessionId)
  // 2. shouldAutoNavigate - Controls auto-navigation behavior (disabled on join-game while in lobby)
  const shouldMonitorSession = Boolean(
    isStudentRoutePath &&
    sessionId &&
    !userHasExplicitlyExited
  );

  const shouldAutoNavigate = Boolean(
    shouldMonitorSession &&
    !isOnJoinGameInLobby  // Don't redirect away from /join-game while game is in lobby
  );

  // Debug URL state only when location changes
  useEffect(() => {
    if (import.meta.env.DEV) {
      debugUrlState('StudentGameSessionMonitor');

      // Enhanced debug logging with URL parsing diagnostics
      logger.debug('StudentGameSessionMonitor Debug:', {
        currentPath: location.pathname,
        sessionIdFromURL: sessionId,
        robustParams,
        urlDiagnostics,
        urlParams,
        urlValidation,
        isStudentRoute: isStudentRoutePath,
        isOnTeacherRoute,
        isOnMainLobby,
        exitInProgress,
        isOnJoinGameInLobby,
        userHasExplicitlyExited,
        shouldMonitorSession,
        shouldAutoNavigate,
        hasCurrentSession: !!currentSession,
        currentSessionId: currentSession?.id,
        currentSessionState: currentSession?.gameState
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, sessionId]); // Only re-run when URL actually changes

  useEffect(() => {
    if (urlDiagnostics.hasDiscrepancy) {
      logger.error('URL Parameter Parsing Discrepancy:', urlDiagnostics);
    }
  }, [urlDiagnostics]);

  // Stable string keys so the effect only re-runs when the list contents change,
  // not on every render where urlValidation's arrays are rebuilt with same contents.
  const urlValidationMissingParamsKey = urlValidation.missingParams.join(',');
  const urlValidationRecommendationsKey = urlValidation.recommendations.join(',');

  useEffect(() => {
    if (!isStudentRoutePath || urlValidation.isValid) {
      return;
    }

    logger.debug('Student URL Validation Issues:', {
      missingParams: urlValidation.missingParams,
      recommendations: urlValidation.recommendations,
      pathname: location.pathname
    });
    // Arrays on urlValidation are new references each render; we intentionally
    // subscribe to the stringified identity instead via *Key deps above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isStudentRoutePath,
    location.pathname,
    urlValidation.isValid,
    urlValidationMissingParamsKey,
    urlValidationRecommendationsKey
  ]);

  // Enable auto-navigation for students in game sessions only
  const { isNavigating, sessionState, localState } = useStudentAutoNavigation(shouldAutoNavigate);

  useEffect(() => {
    latestSessionStateRef.current = currentSession?.gameState ?? null;
  }, [currentSession?.gameState]);

  // Monitor and select the game session if sessionId is provided (for students only)
  useEffect(() => {
    if (shouldMonitorSession && sessionId && (!currentSession || currentSession.id !== sessionId)) {
      logger.debug('Student Monitor: Selecting game session:', sessionId);
      selectSession(sessionId);
    }
  }, [shouldMonitorSession, sessionId, currentSession, selectSession]);

  // Subscribe to real-time session updates while the student is connected
  // NOTE: This uses shouldMonitorSession (not shouldAutoNavigate) so the listener
  // is always active when student has a sessionId, even on join-game waiting screen
  useEffect(() => {
    if (!shouldMonitorSession || !sessionId) {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      return;
    }

    logger.debug('Student Monitor: Setting up real-time listener for session', sessionId);

    const unsubscribe = dbService.subscribeToGameSession(sessionId, (updatedSession) => {
      logger.debug('Student Monitor: Real-time session update received', {
        sessionId,
        gameState: updatedSession.gameState,
        currentQuestionIndex: updatedSession.currentQuestionIndex,
        playersCount: updatedSession.players.length,
        netsCount: updatedSession.activeNetProjectiles?.length || 0,
        timestamp: new Date().toISOString(),
        prevState: latestSessionStateRef.current
      });

      updateSessionActivity();

      const validation = validateSessionConsistency(sessionId, playerId);
      if (!validation.isConsistent) {
        logger.warn('Session consistency issue detected:', validation);
      }

      logger.debug('Student Monitor: Updating store directly with normalized session', {
        playersCount: updatedSession.players.length,
        immobilizedPlayers: updatedSession.players
          .filter(p => p.isImmobilized)
          .map(p => ({
            id: p.id,
            name: p.name,
            isImmobilized: p.isImmobilized,
            immobilizedUntil: p.immobilizedUntil,
            immobilizedUntilType: typeof p.immobilizedUntil
          }))
      });
      setCurrentSessionDirect(updatedSession);
      logger.debug('Student Monitor: Session state synchronized via real-time update');

      const currentGameState = updatedSession.gameState;
      const prevGameState = prevGameStateRef.current;
      prevGameStateRef.current = currentGameState;

      // Verify RTDB connectivity when entering 'running' phase.
      // Each client (student, spectator) runs a quick probe write. If RTDB is
      // unreachable, it's marked unhealthy and all operations fall back to Firestore.
      if (currentGameState === 'running' && prevGameState !== 'running' && !rtdbActiveRef.current) {
        if (arenaRealtimeService.isAvailable()) {
          arenaRealtimeService.verifyConnection(updatedSession.id).then(healthy => {
            rtdbActiveRef.current = healthy;
          });
        }
      }

      // Detach RTDB listeners when leaving 'running' phase
      if (currentGameState !== 'running' && prevGameState === 'running' && rtdbActiveRef.current) {
        rtdbActiveRef.current = false;
        arenaRealtimeService.detachAllListeners();
        // Note: cleanup (RTDB data deletion) is done by the teacher's client, not students
      }
    });

    unsubscribeRef.current = unsubscribe;

    refreshSession(sessionId).catch((error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('Student Monitor: Failed to do initial refresh', {
        sessionId,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });
    });

    return () => {
      try {
        if (unsubscribeRef.current === unsubscribe) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        } else {
          unsubscribe();
        }
      } catch (error) {
        logger.warn('Error during cleanup:', error);
      }
      if (rtdbActiveRef.current) {
        rtdbActiveRef.current = false;
        arenaRealtimeService.detachAllListeners();
      }
    };
  }, [shouldMonitorSession, sessionId, playerId, refreshSession, setCurrentSessionDirect, validateSessionConsistency]);

  // Real-time subscription is now active via Firebase listeners above

  // Log navigation state for debugging (students only)
  useEffect(() => {
    if (shouldAutoNavigate && sessionId && isNavigating) {
      logger.debug('Student Auto-Navigation: State sync in progress', {
        sessionState,
        localState,
        isNavigating
      });
    }
  }, [shouldAutoNavigate, isNavigating, sessionState, localState, sessionId]);

  return (
    <>
      {children}
      <StudentDebugPanel
        isVisible={showDebugPanel}
        onToggle={() => setShowDebugPanel(!showDebugPanel)}
      />
    </>
  );
};
